import { GetServerSidePropsContext, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Swiper from 'swiper'

import { blockSectionColumnTransformer } from 'src/relay/Transformers/BlockSectionColumnTransformer'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import AdsFormReplyModal from '../../components/AdsFormReplyModal'
import { BlockCategory } from '../../forms/AdsForm/types'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { initWithLangAndLabels } from '../../i18n'
import { actions, selectors } from '../../redux'
import { useAdsMediaGalleryProps } from '../../relay/Classified/useAdsMediaGalleryProps'
import { useAdsMediaSliderModalProps } from '../../relay/Classified/useAdsMediaSliderModalProps'
import useBlock from '../../relay/Classified/useBlock'
import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import useFooter from '../../relay/Footer/useFooter'
import useAnswerClassified from '../../relay/Modals/useAnswerClassifiedProps'
import { adsRelatedTransformer } from '../../relay/Transformers/AdsRelatedTransformer'
import { bannerTitleTextButtonTransformer } from '../../relay/Transformers/BannerTitleTextButtonTransformer'
import { ClassifiedAuthorCardTransformer } from '../../relay/Transformers/ClassifiedAuthorCardTransformer'
import { ClassifiedDescriptionTransformer } from '../../relay/Transformers/ClassifiedDescriptionTransformer'
import { ClassifiedIntroTransformer } from '../../relay/Transformers/ClassifiedIntroTransformer'
import { ClassifiedShareSocialNetworkTransformer } from '../../relay/Transformers/ClassifiedShareSocialNetworkTransformer'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'
import useHeaderBis from '../../relay/header/useHeaderBis'
import RestApiClient from '../../rest/client'
import classifiedBySlug from '../../rest/requests/classifieds/classifiedBySlug'
import getConfiguration from '../../rest/services/getConfiguration'
import { type Classified, Status } from '../../rest/types/Classified'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'
import AdsTemplate, { AdsTemplateProps } from '../../templates/AdsTemplate'
import tracking from '../../tracking'

type Props = PageProps & {
  classified: Classified
}

const Classified: NextPage<Props> = ({ classified, configuration }) => {
  const headerProps = useHeaderBis(configuration, false)
  const footerProps = useFooter(configuration)
  const user = useSelector(selectors.auth.user)
  const isConnected = useSelector(selectors.auth.isConnected)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isMediaSliderModalOpen, setIsMediaSliderModalOpen] =
    useState<boolean>(false)
  const sliderRef = useRef<Swiper>(null)
  const router = useRouter()

  const [{ data: classifiedsByCategoryResponse }, handleLoad] =
    useServiceRequest(
      selectors.classifieds.getLatestClassifiedsByCategory,
      actions.classifieds.getLatestClassifiedsByCategoryRequest,
      actions.classifieds.getLatestClassifiedsByCategoryReset
    )

  const [, increaseView] = useServiceRequest(
    selectors.classifieds.increaseView,
    actions.classifieds.increaseViewRequest,
    actions.classifieds.increaseViewReset
  )

  useEffect(() => {
    dispatch(actions.classifieds.setCurrentClassifiedSlug(null))
  }, [dispatch])

  useEffect(() => {
    handleLoad({
      category: classified?.classifiedCategory?.form,
      limit: '3',
      excludedId: classified.id,
    })
  }, [classified?.classifiedCategory?.form, classified.id, handleLoad])

  useEffect(() => {
    if (isConnected && user) {
      const hasSeen = classified.classified_views?.find(
        (v) => v?.user?.id === user.id
      )

      if (!hasSeen) {
        increaseView({
          user: user.id,
          classified: classified.id,
        })
      }
    }
  }, [increaseView, classified, user, isConnected])

  useEffect(() => {
    tracking.ad_view(classified)
  }, [classified])

  const block = useBlock({ classified })

  const answerModalProps = useAnswerClassified({
    classifiedId: classified.id,
    fullClassified: classified,
    category: classified.classifiedCategory,
    configuration,
  })

  const props: AdsTemplateProps = {
    backLink: {
      label: t('backLink_label'),
      onClick: () => {
        if (window.history.state && window.history.state.idx > 0) {
          router.back()
        } else {
          Router.pushRoute(routes.page, {
            slug: 'accueil-petites-annonces',
          })
        }
        router.back()
      },
    },
    adsIntro: ClassifiedIntroTransformer({
      classified,
      t,
      isConnected,
      dispatch,
    }),
    adsDescription: ClassifiedDescriptionTransformer({ classified, t }),
    adsAuthorCard: ClassifiedAuthorCardTransformer({
      classified,
      t,
      configuration,
    }),
    ...block,
    adsShareSocialNetwork: ClassifiedShareSocialNetworkTransformer({
      classified,
      t,
    }),
    adsMediaGallery: useAdsMediaGalleryProps({
      classified,
      t,
      setIsMediaSliderModalOpen,
      sliderRef,
    }),
    adsMediaSliderModal: useAdsMediaSliderModalProps({
      classified,
      setIsMediaSliderModalOpen,
      isMediaSliderModalOpen,
      sliderRef,
    }),
    answerAdButton: {
      variant: ActionButtonVariant.quaternary,
      ...((isConnected ||
        classified?.classifiedCategory?.form === BlockCategory.OperatingAid) &&
      classified?.status !== Status.Archived
        ? {
            onClick: () => {
              dispatch &&
                dispatch(actions.modals.setAnswerClassifiedModalIsOpen(true))
            },
            label: t('answer_classified_button_text'),
          }
        : {
            onClick: () => {
              dispatch &&
                dispatch(
                  actions.classifieds.setCurrentClassifiedSlug(classified.slug)
                )
              Router.pushRoute(routes.loginOrRegister)
            },
            label: t('answer_classified_not_connected_button_text'),
          }),
    },
    bannerTitleTextButton: bannerTitleTextButtonTransformer({
      t,
      isConnected,
      classified,
      onButtonClick:
        (isConnected ||
          classified?.classifiedCategory?.form ===
            BlockCategory.OperatingAid) &&
        classified?.status !== Status.Archived
          ? () => dispatch(actions.modals.setAnswerClassifiedModalIsOpen(true))
          : () => {
              dispatch &&
                dispatch(
                  actions.classifieds.setCurrentClassifiedSlug(classified.slug)
                )
              Router.pushRoute(routes.loginOrRegister)
            },
    }),
    blockColumnSection: blockSectionColumnTransformer({
      t,
    }),
    ...(classifiedsByCategoryResponse &&
      classifiedsByCategoryResponse.length > 0 && {
        adsRelated: adsRelatedTransformer({
          classifiedsByCategoryResponse,
          t,
        }),
      }),

    headerProps,
    footerProps,
    modals:
      isConnected ||
      classified?.classifiedCategory?.form === BlockCategory.OperatingAid ? (
        <AdsFormReplyModal {...answerModalProps} />
      ) : (
        <></>
      ),
  }

  return <AdsTemplate {...props} />
}

export default Classified

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const slug: string = String(context?.params?.slug ?? '')

  const [configuration, rs] = await Promise.all([
    getConfiguration(),
    RestApiClient.call(classifiedBySlug({ slug })),
  ])
  const classified = rs?.data
  const i18n = initWithLangAndLabels('fr', configuration.translations)

  const classifiedSeo = {
    metaTitle: classified?.title,
    metaDescription: classified?.summary,
    metaImage: classified?.image,
  }

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!classified) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
    }
  }

  return {
    props: {
      classified,
      metas: SeoTransformer(
        Router.getRouteUrl(routes.classified, context?.params),
        classifiedSeo
      ),
      configuration,
      i18n,
    },
  }
}
